import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { memo, useCallback, useEffect, useState } from "react";
import { Handle, Position } from "reactflow";
import UploadDatasetsNode from "../UploadDatasetsNode/UploadDatasetsNode";
import "./RAG.css";
import { getDataset } from "../../../../../../utils/IndexedDBDexieUtility";
import { useSelector } from "react-redux";
import { updateLocalStorage } from "../../../../AppDrawerNav";
import {
  CloseOutlined,
  ExpandMore,
  Fullscreen,
  FullscreenExit,
  HelpOutline,
} from "@mui/icons-material";
import {
  childNodeNavText,
  queryloopNodeStyles,
  queryloopParamsContainer,
  tableContainerRag,
  topNavNode,
  topNavNodeHidden,
  topNavNodeText,
} from "../GeneralNodeStyles";
import { tableStyles } from "../../../../../../Constants/tableStyles";
import {
  botDataDefault,
  retrievalDefault,
} from "../../../../../../utils/globalBotData";
import {
  mapDocumentParser,
  mapRetrievalMethodHyperparameter,
  mapRetrievalSequence,
} from "../../../../../../utils/utilityFunctions";
import { ChunkSizeNode } from "./RetrievalComponents/ChunkSizeNode";
import MetricType from "./RetrievalComponents/MetricType";
import RetrievalMethod from "./RetrievalComponents/RetrievalMethod";
import RerankMethod from "./RetrievalComponents/RerankMethod";
import TopK from "./RetrievalComponents/TopK";
import EmbeddingModel from "./RetrievalComponents/EmbeddingModel";
import DocumentParser from "./RetrievalComponents/DocumentParser";
import QueryTransforms from "./RetrievalComponents/QueryTransforms";
import theme, { scrollbarStyles } from "../../../../../../Constants/theme";
import { foundationalStyles } from "../LLMNode/FountaionalStyles";
import { deleteIconStyles } from "../../../../../../Constants/buttonStyles";

const QueryloopNode = memo(({ data }) => {
  const organization = useSelector((state) => state.organization);
  const { botData, setBotData, nodeId, handleRetrievalTutorial, setOpenKeys } =
    data;
  const [chunkSize, setChunkSize] = useState(
    botData.workflow[`${nodeId}`].chunksize || retrievalDefault.chunksize
  );
  const [metricType, setMetricType] = useState(
    botData.workflow?.[`${nodeId}`]?.metric || retrievalDefault.metric
  );
  const [retrievalMethod, setRetrievalMethod] = useState(
    botData.workflow?.[`${nodeId}`]?.retrieval_method ||
      retrievalDefault.retrieval_method
  );
  const [rerank, setRerank] = useState(
    botData.workflow?.[`${nodeId}`]?.rerank_method ||
      retrievalDefault.rerank_method
  );
  const [topk, setTopk] = useState(
    botData.workflow?.[`${nodeId}`]?.top_k || retrievalDefault.top_k
  );
  const [embedding, setEmbedding] = useState(
    botData.workflow?.[`${nodeId}`]?.embedding_model ||
      retrievalDefault.embedding_model
  );
  const [parser, setParser] = useState(
    botData.workflow?.[`${nodeId}`]?.parser || retrievalDefault.parser
  );
  const [queryTransform, setQueryTransform] = useState(
    botData.workflow?.[`${nodeId}`]?.query_preprocess_method ||
      retrievalDefault.query_preprocess_method
  );
  //store index of chains selected
  const [selectedChainItems, setSelectedChainItems] = useState([]);
  // Store prompts at the parent level
  const [prompts, setPrompts] = useState(
    botDataDefault.query_preprocessing_prompts.retrieval_1
  );
  // store current chaining prompt
  const [chainingPromptsQT, setChainingPromptsQT] = useState("");
  // Array to store chaning prompts list
  const [sequenceHistory, setSequenceHistory] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [structured_datasets, setStructured_datasets] = useState([]);
  const [totalCombinations, setTotalCombinations] = useState(
    localStorage.getItem("combinations") || 1
  );
  const [graphRagCredentials, setGraphRagCredentials] = useState(
    botDataDefault.neo4j_credentials
  );

  const [neo4jPrompts, setNeo4jPrompts] = useState(
    botDataDefault.neo_4j_prompts
  );

  const [parsingInstructions, setParsingInstructions] = useState(
    JSON.parse(
      localStorage.getItem(`parsing_instructions_${nodeId}`) ||
        JSON.stringify(botDataDefault.parsing_instructions)
    )
  );

  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState("document-parser");

  // Handle changing expanded panel
  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    // Helper function to parse JSON safely
    const loadData = async () => {
      try {
        // Load datasets from IndexedDB
        const storedDatasets = await getDataset(`dataset_${nodeId}`);

        if (storedDatasets) {
          setDatasets(storedDatasets);
        }

        const storedStructuredDatasets = await getDataset(
          `structured_dataset_${nodeId}`
        );

        if (storedDatasets) {
          setStructured_datasets(storedStructuredDatasets);
        }

        // Load other states from localStorage as before
        const updateStateFromLocalStorage = (key, setter) => {
          const storedValue = localStorage.getItem(key);
          if (storedValue) {
            const parsedValue = JSON.parse(storedValue);
            if (parsedValue) {
              setter(parsedValue);
            }
          }
        };

        // Update states from localStorage
        updateStateFromLocalStorage(`chunkSize_${nodeId}`, setChunkSize);
        updateStateFromLocalStorage(`metricType_${nodeId}`, setMetricType);
        updateStateFromLocalStorage(
          `retrievalMethod_${nodeId}`,
          setRetrievalMethod
        );
        updateStateFromLocalStorage(`rerank_${nodeId}`, setRerank);
        updateStateFromLocalStorage(`topk_${nodeId}`, setTopk);
        updateStateFromLocalStorage(`embedding_${nodeId}`, setEmbedding);
        updateStateFromLocalStorage(`parser_${nodeId}`, setParser);
        updateStateFromLocalStorage(
          `queryTransform_${nodeId}`,
          setQueryTransform
        );
        updateStateFromLocalStorage(
          `queryTransform_prompts_${nodeId}`,
          setPrompts
        );
        updateStateFromLocalStorage(
          `parsing_instructions_${nodeId}`,
          setParsingInstructions
        );
        updateStateFromLocalStorage(
          `neo4j_credentials`,
          setGraphRagCredentials
        );
        updateStateFromLocalStorage(
          `neo_4j_prompts_${nodeId}`,
          setNeo4jPrompts
        );
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadData();
  }, [nodeId, setBotData]);

  useEffect(() => {
    calculateAndStoreCombinations();
  }, [
    embedding,
    topk,
    rerank,
    retrievalMethod,
    metricType,
    chunkSize,
    parser,
    queryTransform,
  ]);

  const calculateAndStoreCombinations = () => {
    const categories = [
      "embedding",
      "topk",
      "rerank",
      "retrievalMethod",
      "metricType",
      "chunkSize",
      "parser",
      "queryTransform",
    ];

    const data = {
      embedding,
      topk,
      rerank,
      retrievalMethod,
      metricType,
      chunkSize,
      parser,
      queryTransform,
    };

    const getTrueValues = (cat) => {
      if (!data[cat] || typeof data[cat] !== "object") {
        console.warn(`Invalid data for category: ${cat}`);
        return [];
      }
      return Object.keys(data[cat]).filter((key) => data[cat][key] === true);
    };

    let totalCombinations = 1;
    let hasEmptyCategory = false;

    for (const category of categories) {
      const selectedValues = getTrueValues(category);
      if (selectedValues.length === 0) {
        hasEmptyCategory = true;
        break;
      }
      totalCombinations *= selectedValues.length;
    }

    if (hasEmptyCategory) {
      localStorage.setItem("combinations", "0");
    } else {
      localStorage.setItem("combinations", totalCombinations.toString());
    }

    setTotalCombinations(totalCombinations);
  };

  const handleOpenSideBar = useCallback((infotype, event) => {
    event.stopPropagation(); // Prevent event from bubbling up to ReactFlow
    updateLocalStorage("open_sidebar", "true");
    updateLocalStorage("type", infotype);
  }, []);

  return (
    <Box
      sx={
        hidden
          ? { ...queryloopNodeStyles }
          : {
              display: "flex",
              flexDirection: "column",
              height: "auto",
              background: theme.main.applicationBackground,
              overflowY: "auto",
              width: "auto",
              borderRadius: "10px",
              ...scrollbarStyles,
            }
      }
    >
      <Box sx={hidden ? topNavNodeHidden : topNavNode} data-tour="option-1b">
        <Typography sx={topNavNodeText}>Retrieval</Typography>
        <div>
          {/* <p>Combination Count: {localStorage.getItem(`combinations`) || 1}</p> */}
        </div>

        <div className="icon-container">
          <Tooltip title="Click for more Information">
            <InfoOutlined
              onClick={(event) =>
                handleOpenSideBar("retrieval_hyperparameters", event)
              }
            />
          </Tooltip>
          <div
            className="icon-container"
            onClick={() => setHidden((prevState) => !prevState)}
            data-tour="option-1f"
          >
            <Tooltip
              title={hidden ? "Show Advanced Options" : "Hide Advanced Options"}
              placement="top"
            >
              {hidden ? (
                <Fullscreen color="white" />
              ) : (
                <FullscreenExit color="white" />
              )}
            </Tooltip>
          </div>
          <Tooltip title="Need Help?">
            <HelpOutline onClick={() => handleRetrievalTutorial()} />
          </Tooltip>
          <Tooltip title="Delete this tool">
            <IconButton
              sx={{ ...deleteIconStyles, ml: 1 }}
              onClick={() => data.handleDeleteSelected(nodeId)}
            >
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: hidden ? "auto" : "900px",
        }}
      >
        {!hidden && (
          <Box sx={queryloopParamsContainer}>
            <Box sx={topNavNode}>
              <Typography sx={childNodeNavText}>Select Parameters</Typography>
              <div>
                <InfoOutlined
                  onClick={(event) =>
                    handleOpenSideBar("retrieval_hyperparameters", event)
                  }
                />
              </div>
            </Box>
            <Box
              sx={{
                minHeight: "500px",
                maxHeight: "auto",
                height: "auto",
                m: 2,
                mt: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Accordion
                disableGutters={true}
                sx={foundationalStyles.accordion}
                expanded={expanded === "document-parser"}
                onChange={handleChangeAccordion("document-parser")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMore sx={{ color: theme.textColors.dark.primary }} />
                  }
                  aria-controls="document-parser-content"
                  id="document-parser-header"
                >
                  <AccordionDetails
                    sx={{ ...foundationalStyles.details, borderTop: "none" }}
                  >
                    <Box
                      sx={{
                        ...foundationalStyles.checkboxRow,
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography>Document Parser</Typography>
                      <InfoOutlined
                        fontSize="small"
                        onClick={(event) =>
                          handleOpenSideBar("document_parser", event)
                        }
                      />
                    </Box>
                  </AccordionDetails>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <DocumentParser
                    data={data}
                    parser={parser}
                    setParser={setParser}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                    parsingInstructions={parsingInstructions}
                    setParsingInstructions={setParsingInstructions}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                sx={foundationalStyles.accordion}
                expanded={expanded === "chunk-size-node"}
                onChange={handleChangeAccordion("chunk-size-node")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="chunk-size-node-content"
                  id="chunk-size-node-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Chunk Size</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenSideBar("chunk_size", event)
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <ChunkSizeNode
                    data={data}
                    chunkSize={chunkSize}
                    setChunkSize={setChunkSize}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                sx={foundationalStyles.accordion}
                expanded={expanded === "retrieval-method"}
                onChange={handleChangeAccordion("retrieval-method")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="retrieval-method-content"
                  id="retrieval-method-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Document Pre-processing</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenSideBar("retrieval_method", event)
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <RetrievalMethod
                    data={data}
                    retrievalMethod={retrievalMethod}
                    setRetrievalMethod={setRetrievalMethod}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                    graphRagCredentials={graphRagCredentials}
                    setGraphRagCredentials={setGraphRagCredentials}
                    neo4jPrompts={neo4jPrompts}
                    setNeo4jPrompts={setNeo4jPrompts}
                    setOpenKeys={setOpenKeys}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                sx={{
                  ...foundationalStyles.accordion,
                }}
                expanded={expanded === "embedding-model"}
                onChange={handleChangeAccordion("embedding-model")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="embedding-model-content"
                  id="embedding-model-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Embedding Model</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenSideBar("embedding_model", event)
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <EmbeddingModel
                    data={data}
                    embedding={embedding}
                    setEmbedding={setEmbedding}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  ...foundationalStyles.accordion,
                }}
                disableGutters={true}
                expanded={expanded === "metric-type"}
                onChange={handleChangeAccordion("metric-type")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="metric-type-content"
                  id="metric-type-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Distance Metrics</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenSideBar("metrics_type", event)
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <MetricType
                    data={data}
                    metricType={metricType}
                    setMetricType={setMetricType}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  ...foundationalStyles.accordion,
                }}
                disableGutters={true}
                expanded={expanded === "query-transforms"}
                onChange={handleChangeAccordion("query-transforms")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="query-transforms-content"
                  id="query-transforms-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Query Transformations</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenSideBar("query_transformations", event)
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <QueryTransforms
                    data={data}
                    queryTransform={queryTransform}
                    setQueryTransform={setQueryTransform}
                    prompts={prompts}
                    setPrompts={setPrompts}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                    chainingPromptsQT={chainingPromptsQT}
                    setChainingPromptsQT={setChainingPromptsQT}
                    sequenceHistory={sequenceHistory}
                    setSequenceHistory={setSequenceHistory}
                    setSelectedChainItems={setSelectedChainItems}
                    selectedChainItems={selectedChainItems}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                sx={foundationalStyles.accordion}
                expanded={expanded === "top-k"}
                onChange={handleChangeAccordion("top-k")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="top-k-content"
                  id="top-k-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Top K</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) => handleOpenSideBar("top_k", event)}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <TopK
                    data={data}
                    topk={topk}
                    setTopk={setTopk}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters={true}
                sx={foundationalStyles.accordion}
                expanded={expanded === "rerank-method"}
                onChange={handleChangeAccordion("rerank-method")}
              >
                <AccordionSummary
                  sx={foundationalStyles.summary}
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.textColors.dark.primary }}
                    />
                  }
                  aria-controls="rerank-method-content"
                  id="rerank-method-header"
                >
                  <Box
                    sx={{
                      ...foundationalStyles.checkboxRow,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography>Rerank Method</Typography>
                    <InfoOutlined
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenSideBar("post_retrieval", event)
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={foundationalStyles.details}>
                  <RerankMethod
                    data={data}
                    rerank={rerank}
                    setRerank={setRerank}
                    organization={organization}
                    calculateAndStoreCombinations={
                      calculateAndStoreCombinations
                    }
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        )}

        <div
          style={{
            marginTop: hidden ? "0px" : "20px",
            marginLeft: hidden ? "20px" : "0px",
            marginBottom: hidden ? "20px" : "0px",
            marginRight: "10px",
            paddingTop: hidden ? "20px" : "0px",
            width: "100%",
          }}
        >
          <UploadDatasetsNode
            data={data}
            structured_datasets={structured_datasets}
            datasets={datasets}
            hidden={hidden}
          />

          {!hidden && (
            <Box sx={tableContainerRag} data-tour="option-1g">
              <TableContainer sx={tableStyles.tableContainer}>
                <Table aria-label="simple table">
                  <TableHead
                    sx={{
                      ...tableStyles.tableHead,
                      fontSize: theme.typography.fontSize.md,
                    }}
                  >
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableHeaderCell, width: "30%" }}
                        component="th"
                        align="left"
                      >
                        Parameter
                      </TableCell>
                      <TableCell
                        sx={tableStyles.tableHeaderCell}
                        component="th"
                        align="left"
                      >
                        Selected Values
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ fontSize: theme.typography.fontSize.md }}>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Chunk Size
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(chunkSize || {})
                          .filter((_) => chunkSize?.[_] === true)
                          .join(", ")}{" "}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        {" "}
                        Document Parser{" "}
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(parser || {})
                          .filter((key) => parser?.[key] === true)
                          .map((key) => mapDocumentParser(key))
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Distance Metric
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(metricType || {})
                          .filter((_) => metricType?.[_] === true)
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Retrieval Method
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(retrievalMethod || {})
                          .filter((key) => retrievalMethod?.[key] === true)
                          .map((key) => mapRetrievalSequence(key))
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Query Preprocessing
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(queryTransform || {})
                          .filter((key) => queryTransform?.[key] === true)
                          .map((key) => mapRetrievalMethodHyperparameter(key))
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Rerank Method
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(rerank || {})
                          .filter((_) => rerank?.[_] === true)
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        TopK
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(topk || {})
                          .filter((_) => topk?.[_] === true)
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Embedding Model
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {Object.keys(embedding || {})
                          .filter((_) => embedding?.[_] === true)
                          .join(", ")}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={tableStyles.tableRow}>
                      <TableCell
                        sx={{ ...tableStyles.tableCell, width: "30%" }}
                        align="left"
                      >
                        Combination Count
                      </TableCell>
                      <TableCell sx={tableStyles.tableCell}>
                        {totalCombinations}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </div>
      </Box>

      <Handle
        type="source"
        position={Position.Right}
        id="a"
        style={{
          top: hidden ? "70%" : "2%",
          backgroundColor: "var(--buttonBackground)",
          height: "14px",
          width: "14px",
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="b"
        style={{
          top: hidden ? "70%" : "2%",
          backgroundColor: "var(--buttonBackground)",
          height: "14px",
          width: "14px",
        }}
      />
    </Box>
  );
});

export default QueryloopNode;
